import { ApiMatchEventIdGet200ResponseDataMatch } from '@/api/livescore/generated';
import LivescoreEntityLink from '@/components/atoms/LivescoreEntityLink/LivescoreEntityLink';
import ArrowRightCorner from '@/components/icons/ArrowRightCorner';
import { PREMATCH_STATUSES, useLivescoreMatch } from '@/context/LivescoreMatchContext';
import { cn } from '@/utils/cn';
import { blackStatuses, getMatchStatus, grayStatuses, greenStatuses, redStatuses } from '@/utils/getMatchStatus';
import Image from 'next/image';

function formatISODate(isoDate: string) {
  const date = new Date(isoDate);

  // Sprawdzenie, czy data jest poprawna
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Miesiące są 0-indeksowane
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}.${month}.${year} - ${hours}:${minutes}`;
}

const StatusBadge = ({ status }: { status: ApiMatchEventIdGet200ResponseDataMatch['sport_event_status'] }) => {
  const plStatus = getMatchStatus(status);

  return (
    <div
      className={cn(
        'absolute -top-5 sm:-top-2 left-1/2 -translate-x-1/2 py-1.5 px-2 font-extrabold sm:!text-xxs !text-[8px] uppercase rounded',
        status && redStatuses.includes(plStatus!) && 'bg-red-900 text-red-500',
        status &&
          (greenStatuses.includes(plStatus!) || grayStatuses.includes(plStatus!)) &&
          'dark:bg-green-900 bg-primary-150 text-primary-600',
        status && blackStatuses.includes(plStatus!) && 'dark:bg-black-500 bg-gray-200 text-gray-400',
      )}
    >
      {plStatus} {status.clock?.played && <>{status.clock?.played?.split(':')[0]}&rsquo;</>}
    </div>
  );
};

const TeamElement = ({
  team,
  winner,
  isAhref,
}: {
  team: ApiMatchEventIdGet200ResponseDataMatch['sport_event']['competitors'][number];
  winner?: boolean;
  isAhref?: boolean;
}) => {
  return (
    <LivescoreEntityLink
      href={`/livescore/team/${team.slug ?? team.id}`}
      className="flex flex-col items-center justify-center w-full sm:gap-6 gap-2 px-4"
      isAhref={isAhref}
    >
      {team.logo_url ? (
        <div className="relative w-10 h-10 md:w-20 md:h-20">
          <Image
            src={team.logo_url}
            alt={team.name}
            layout="fill"
            sizes="(max-width: 600px) 40px, 80px"
            objectFit="contain"
          />
        </div>
      ) : (
        <div className="sm:w-20 sm:h-20 w-10 h-10 bg-green-400 rounded-full flex items-center justify-center overflow-hidden">
          <span className="sm:text-xl text-sm font-bold text-gray-800 m-0 pt-0.5">{team.abbreviation}</span>
        </div>
      )}
      <div className='flex sm:gap-1 relative'>
        <p className="dark:text-white text-black font-semibold sm:text-lg text-[10px] text-center line-clamp-2 overflow-ellipsis w-full">
          {team.name} {team.abbreviation && `(${team.abbreviation})`}
          {winner && (
            <span className='inline-block align-top ml-0.5 scale-50 sm:scale-100 origin-top-left'>
              <ArrowRightCorner color="#31C46C" width={16} />
            </span>
          )}
        </p>
      </div>
    </LivescoreEntityLink>
  );
};

const LivescoreMatchResult = () => {
  const { match } = useLivescoreMatch();

  return (
    <div className="w-full dark:bg-black-500 bg-white_theme-50 sm:pb-12 pb-6 relative sm:pt-4 pt-2">
      {/* gradient */}
      <div className="absolute inset-0 match-linear-gradient z-0" aria-hidden />
      <div className="relative z-10">
        <div className="w-full text-center py-3">
          <p className="text-gray-400 font-medium sm:text-xxs text-[7px]">
            {match.sport_event.start_time ? formatISODate(match.sport_event.start_time) : ''}
          </p>
        </div>
        <div className="flex items-center justify-center pt-4 sm:pt-2">
          <div className="flex items-center w-full justify-between relative">
            <TeamElement team={match.sport_event.competitors[0]} isAhref={match.seoLinks} winner={match.sport_event_status.aggregate_winner_id === match.sport_event.competitors[0].id} />
            <p className="text-3xl sm:text-[50px] text-primary-600 font-gameplay space-x-1 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <span>{match.sport_event_status.home_score ?? '-'}</span>
              <span>:</span>
              <span>{match.sport_event_status.away_score ?? '-'}</span>
            </p>
            {match.sport_event_status?.period_scores?.[3] && (
              <p className="text-[13px] sm:text-[10px] text-gray-400 space-x-1 absolute left-1/2 top-[88%] -translate-x-1/2 -translate-y-1/2">
                ({match.sport_event_status?.period_scores?.[3].home_score ?? 'x'}-{match.sport_event_status?.period_scores?.[3].away_score ?? 'x'})
              </p>
            )}
            <TeamElement team={match.sport_event.competitors[1]} isAhref={match.seoLinks} winner={match.sport_event_status.aggregate_winner_id === match.sport_event.competitors[1].id} />

            {/* live info with position absolute */}
            {!PREMATCH_STATUSES.includes(match.sport_event_status.status) && (
              <StatusBadge status={match.sport_event_status} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LivescoreMatchResult;
